import api from '@/api/api'
import {connectToWs, getWsStatus, onMessage, onStatus, subscribe, unsubscribe} from '@/services/websocket'
import {
    lastEventGameTime,
    lastTimelineGameTime,
    mergeBattle,
    mergeEvents,
    mergeTimeline,
    TIMELINE_STEP,
    timelinePointFromBattle,
} from '@/common/mlbbBroadcast'

export const BATTLE_UPDATED_EVENT = 'mlbb-battle.updated'
export const BATTLE_EVENTS_EVENT = 'mlbb-battle.events'
export const battleChannel = (battleId) => `mlbb-battles.${battleId}`

const EVENTS_PER_PAGE = 500
const SILENT = {skipErrorToast: true}

/**
 * Live-данные одной игры (battle) по рекомендуемому сценарию бэка:
 * REST для первой загрузки → WebSocket mlbb-battles.{id} → после реконнекта догрузка по REST
 * (battle целиком, события и таймлайн с since_game_time).
 *
 * Состояние наружу отдаётся колбэками, чтобы компонент сам решал, что перерисовывать:
 *   onBattle(battle)            — новое состояние battle (уже слитое со служебными полями опроса)
 *   onEvents(allEvents, fresh)  — вся лента и только что пришедшие события
 *   onTimeline(points)          — точки для графиков (REST + точка с каждого тика updated)
 *   onStatus(status)            — 'connecting' | 'open' | 'closed' (соединение WebSocket)
 *   onError(error)              — ошибка REST-загрузки
 *
 * eventsOnly — только лента событий по публичным ручкам (OBS без входа: сам battle приходит из трансляции,
 * а админская ручка battle без токена ответит 401 и выкинет на логин).
 */
export function createBattleLive({battleId, onBattle, onEvents, onTimeline, onStatus: onWsStatus, onError, eventsOnly = false} = {}) {
    const channel = battleChannel(battleId)
    let battle = null
    let events = []
    let timeline = []
    let stopped = false
    let loaded = false
    let offMessage = null
    let offStatus = null

    const emitBattle = (next) => {
        if (stopped || !next || next.id !== battleId) return
        battle = mergeBattle(battle, next)
        onBattle && onBattle(battle)
        // отдельного события для графика нет — дописываем точку из тика
        const point = timelinePointFromBattle(battle)
        if (point && point.game_time > lastTimelineGameTime(timeline)) emitTimeline([point])
    }

    const emitEvents = (incoming) => {
        if (stopped) return
        const before = events.length
        events = mergeEvents(events, incoming)
        if (events.length !== before || incoming.length) onEvents && onEvents(events, incoming)
    }

    const emitTimeline = (incoming) => {
        if (stopped) return
        timeline = mergeTimeline(timeline, incoming)
        onTimeline && onTimeline(timeline)
    }

    async function loadBattle() {
        const res = await api.mlbbEsports.battle(battleId, SILENT)
        emitBattle(res?.data?.data)
    }

    async function loadEvents(sinceGameTime) {
        const collected = []
        let page = 1
        let lastPage = 1
        do {
            const params = {per_page: EVENTS_PER_PAGE, page}
            if (sinceGameTime) params.since_game_time = sinceGameTime
            const res = await api.mlbbEsports.battleEvents(battleId, params, SILENT)
            collected.push(...(res?.data?.data || []))
            lastPage = Number(res?.data?.meta?.last_page) || 1
            page += 1
        } while (page <= lastPage && !stopped)
        emitEvents(collected)
    }

    async function loadTimeline(sinceGameTime) {
        const params = {step: TIMELINE_STEP}
        if (sinceGameTime) params.since_game_time = sinceGameTime
        try {
            const res = await api.mlbbEsports.battleTimeline(battleId, params, SILENT)
            emitTimeline(res?.data?.data || [])
        } catch (e) {
            // график вторичен: без таймлайна battle и лента всё равно должны показаться
        }
    }

    async function load({catchUp = false} = {}) {
        try {
            // since_game_time — «строго после»: берём секундой раньше, чтобы не потерять события
            // той же секунды; пересечение с уже пришедшими по WS отсеет mergeEvents
            const since = catchUp ? Math.max(0, lastEventGameTime(events) - 1) : 0
            const timelineSince = catchUp ? lastTimelineGameTime(timeline) : 0
            await Promise.all(eventsOnly
                ? [loadEvents(since)]
                : [loadBattle(), loadEvents(since), loadTimeline(timelineSince)])
            loaded = true
        } catch (error) {
            if (!stopped) onError && onError(error)
        }
    }

    function handleMessage(msg) {
        // Если battle не существует, сервер присылает массив строк — это не наше событие
        if (stopped || !msg || Array.isArray(msg) || !msg.event) return
        if (msg.channel && msg.channel !== channel) return
        if (msg.event === BATTLE_UPDATED_EVENT) {
            emitBattle(msg.data?.mlbb_battle)
        } else if (msg.event === BATTLE_EVENTS_EVENT) {
            const list = msg.data?.mlbb_battle_events
            if (Array.isArray(list) && list.length) emitEvents(list)
        }
    }

    function handleStatus(status) {
        if (stopped) return
        onWsStatus && onWsStatus(status)
        // Реконнект: пока сокета не было, тики и события могли пройти мимо
        if (status === 'open' && loaded) load({catchUp: true})
    }

    return {
        channel,
        start() {
            offMessage = onMessage(handleMessage)
            offStatus = onStatus(handleStatus)
            connectToWs()
            subscribe(channel)
            onWsStatus && onWsStatus(getWsStatus())
            return load()
        },
        /** Перечитать battle по REST (после link/unlink/resync) */
        refresh() {
            return load({catchUp: true})
        },
        stop() {
            stopped = true
            unsubscribe(channel)
            offMessage && offMessage()
            offStatus && offStatus()
        },
        get battle() {
            return battle
        },
        get events() {
            return events
        },
        get timeline() {
            return timeline
        },
    }
}
